import { Grid, Checkbox, FormControlLabel } from "@mui/material";
import {
  AllFilterDivider,
  OptionsGridChildContainer,
  OptionsLabelContainer,
  OptionsCategory,
  OptionsGridContainer,
  OptionsLabel,
  OptionsIconWrapper,
  FilterCategoryContainer,
  OptionsScrollContainer
} from "./sideModal.styled";
import SearchInput from "./searchInput";
import CheckboxChecked from "assets/CheckboxChecked";
import CheckboxUnchecked from "assets/CheckboxUnchecked";
import { useEffect, useRef, useState } from "react";
import { styled, useTheme } from "styled-components";
import { ArrowUp } from "assets/ArrowUp";
import { ArrowDown } from "assets/ArrowDown";
import React from "react";

interface FiltersOptionsProp {
  filter: any;
  updateFilter: any;
  framework: any;
  openCategory: any;
  setOpenCategory: any;
  handleSetActiveFilter: any;
  hasAutoChecked: boolean;
  descriptions?: string[];
  selectedDescription: string[];
  selectedSectionTitle?: string;
  setHasAutoChecked: (val: boolean) => void;
}

function camelCaseToWords(str) {
  return str
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, function (match) {
      return match.toUpperCase();
    })
    .trim();
}

const OptionsContainer = styled("div")`
  display: block;
  margin-bottom: 24px;
`;

function FiltersOptions({
  filter,
  updateFilter,
  framework,
  handleSetActiveFilter,
  hasAutoChecked,
  setHasAutoChecked,
  descriptions,
  selectedDescription,
  selectedSectionTitle,
}: FiltersOptionsProp) {
  const [openCategories, setOpenCategories] = React.useState<string[]>([]);
  const [openSectionTitles, setOpenSectionTitles] = React.useState<string[]>([]);
  const [searchQueries, setSearchQueries] = React.useState<{ [key: string]: string }>({});
  
  // Function to calculate selected items count for each category
  const getSelectedCount = (category) => {
    if (!category || !category.children) return 0;
    return category.children.filter(item => item.isChecked).length;
  };

  const toggleCategory = (category: string) => {
    setOpenCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  };

  const toggleSectionTitle = (sectionTitle: string) => {
    setOpenSectionTitles((prev) =>
      prev.includes(sectionTitle) ? prev.filter((s) => s !== sectionTitle) : [...prev, sectionTitle]
    );
  };


  React.useEffect(() => {
    if (hasAutoChecked || !framework || !filter?.categories) return;

    let matchedAny = false;

    const allUpdatedCategories = filter.categories.map((category) => {
      if (!category.isFilterable || category.isDate) return category;

      const updatedChildren = category.children?.map((child) => {
        const isMatch = child?.name?.toLowerCase() === framework.toLowerCase();

        if (isMatch && !child.isChecked) {
          matchedAny = true;
          updateFilter(true, child.name, category.label);
          setOpenCategories((prev) => [...prev, category.label]);
          // Also auto open the selectedSectionTitle if exists
          if (selectedSectionTitle) setOpenSectionTitles([selectedSectionTitle]);
        }

        return child;
      });

      return { ...category, children: updatedChildren };
    });

    if (matchedAny) {
      setHasAutoChecked(true);
    }
  }, [framework, filter, hasAutoChecked, updateFilter, setHasAutoChecked, selectedSectionTitle]);

  const theme = useTheme();
  const contentRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    console.log("Filter categories updated:", filter.categories);
  },[filter.categories]);

  return (
    <div>
      {console.log("Filter categories in FiltersOptions:", filter.categories)}
      {filter.categories?.map(
        (category, cat_index) => {
          console.log(`Category ${category.label}: isFilterable=${category.isFilterable}, isDate=${category.isDate}, children=${category.children?.length}`);
          return category.isFilterable &&
          !category.isDate ? (
            <OptionsContainer key={`${category.label}_${cat_index}`}>
              <FilterCategoryContainer>
                <OptionsCategory
                  onClick={() => toggleCategory(category.label)}
                  aria-expanded={openCategories.includes(category.label)}
                  aria-label="show more"
                  expanded={openCategories.includes(category.label)}
                >
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ 
                      display: 'inline-block',
                      fontWeight: getSelectedCount(category) > 0 ? '700' : '500',
                      color: getSelectedCount(category) > 0 ? theme.colors.actionButtonColorHvr : 'inherit'
                    }}>
                      {getSelectedCount(category)}/{category.children?.length || 0}
                    </span>
                    <span style={{ marginLeft: '8px' }}>{camelCaseToWords(category.label)}</span>
                  </span>
                  {openCategories.includes(category.label) ? <ArrowUp /> : <ArrowDown />}
                </OptionsCategory>

                {openCategories.includes(category.label) && (
                <div
                  ref={contentRef}
                  style={{
                    overflow: "hidden",
                    transition: "max-height 0.4s ease-in-out",
                  }}
                >
                  <div style={{ padding: '16px 24px 8px 24px' }}>
                    <SearchInput
                      placeholder={`Search ${camelCaseToWords(category.label)}...`}
                      value={searchQueries[category.label] || ''}
                      onChange={(value) => {
                        setSearchQueries(prev => ({
                          ...prev,
                          [category.label]: value
                        }));
                      }}
                    />
                  </div>
                  <OptionsScrollContainer>
                    <OptionsGridContainer container>
                  {category.children
                    ?.filter(item => {
                      const query = searchQueries[category.label] || '';
                      return query === '' || 
                        item.name.toLowerCase().includes(query.toLowerCase());
                    })
                    .map(
                    (item, index) => (
                        <React.Fragment key={`${item.name}_${index}`}>
                          {/* Framework Checkbox */}
                          <OptionsGridChildContainer item xs={6}>
                            <OptionsLabelContainer
                              control={
                                <>
                                  <Checkbox
                                    icon={<CheckboxUnchecked theme={theme} />}
                                    checkedIcon={<CheckboxChecked />}
                                    name={item.name}
                                    onChange={() =>
                                      updateFilter(!item.isChecked, item.name, category.label)
                                    }
                                    checked={item.isChecked}
                                  />
                                  {item.icon && <OptionsIconWrapper>{<item.icon />}</OptionsIconWrapper>}
                                </>
                              }
                              label={
                                <OptionsLabel color={theme.colors.textColor} size={theme.font.medium}>
                                  {item.name}
                                </OptionsLabel>
                              }
                            />
                          </OptionsGridChildContainer>

                          {/* Selected Section Title Checkbox (only for auto-checked framework) */}
                          {framework &&
                            framework.toLowerCase() === item.name.toLowerCase() &&
                            hasAutoChecked &&
                            selectedSectionTitle && (
                              <div style={{ marginLeft: 24, marginTop: 8 }}>
                                <OptionsGridChildContainer item xs={12}>
                                  <OptionsLabelContainer
                                    control={
                                      <Checkbox
                                        icon={<CheckboxUnchecked theme={theme} />}
                                        checkedIcon={<CheckboxChecked />}
                                        name={selectedSectionTitle}
                                        onChange={() => toggleSectionTitle(selectedSectionTitle)}
                                        checked={openSectionTitles.includes(selectedSectionTitle)}
                                      />
                                    }
                                    label={
                                      <OptionsLabel
                                        color={theme.colors.textColor}
                                        size={theme.font.medium}
                                        style={{ fontWeight: "600" }}
                                      >
                                        {selectedSectionTitle}
                                      </OptionsLabel>
                                    }
                                  />
                                </OptionsGridChildContainer>

                                {/* Descriptions drawer */}
                                {openSectionTitles.includes(selectedSectionTitle) && (
                                  <OptionsGridContainer container style={{ marginLeft: 24 }}>
                                    {Array.isArray(descriptions) ? descriptions.map((descName, idx) => {
                                      if (!descName) return null;

                                      const isSelectedDesc = Array.isArray(selectedDescription)
                                        ? selectedDescription.includes(descName)
                                        : descName === selectedDescription;

                                      return (
                                        <OptionsGridChildContainer item xs={6} key={`desc_${idx}`}>
                                          <OptionsLabelContainer
                                            control={
                                              <Checkbox
                                                icon={<CheckboxUnchecked theme={theme} />}
                                                checkedIcon={<CheckboxChecked />}
                                                name={descName}
                                                onChange={() =>
                                                  updateFilter(
                                                    !isSelectedDesc,
                                                    descName,
                                                    selectedSectionTitle
                                                  )
                                                }
                                                checked={isSelectedDesc}
                                              />
                                            }
                                            label={
                                              <OptionsLabel
                                                color={theme.colors.textColor}
                                                size={theme.font.medium}
                                              >
                                                {descName}
                                              </OptionsLabel>
                                            }
                                          />
                                        </OptionsGridChildContainer>
                                      );
                                    }) : null}
                                  </OptionsGridContainer>
                                )}
                              </div>
                            )}
                        </React.Fragment>
                      )
                  )}
                    </OptionsGridContainer>
                  </OptionsScrollContainer>
                </div>
                )}
              </FilterCategoryContainer>
            </OptionsContainer>
          ) : null
        }
      )}
    </div>
  );
}


export default FiltersOptions;